import React from "react"
import Skeleton from "@material-ui/lab/Skeleton"
import { ThemeProvider } from "@material-ui/styles"
import themeViewnia from "components/utils/Theme"

const PostBody = () => {
  const [loading, setLoading] = React.useState(true)
  const [lastPost, setLastPost] = React.useState(null)
  const [posts, setPosts] = React.useState([])
  const [mainImageLoaded, setMainImageLoaded] = React.useState(false)
  const [postsImagesLoaded, setPostsImagesLoaded] = React.useState({})

  // Fetch data when component mounts
  React.useEffect(() => {
    setLoading(true)
    fetch("/posts.json")
      .then((res) => res.json())
      .then((data) => {
        setLastPost(data.data.last_post)
        setPosts(data.data.posts || [])
        setLoading(false)
      })
      .catch((error) => {
        console.error("Error fetching posts:", error)
        setLoading(false)
      })
  }, [])

  // Reset main image state when lastPost changes
  React.useEffect(() => {
    setMainImageLoaded(false)
  }, [lastPost])

  // Initialize posts images state
  React.useEffect(() => {
    if (posts && posts.length > 0) {
      const loadedMap = {}
      posts.forEach((post, idx) => {
        loadedMap[idx] = false
      })
      setPostsImagesLoaded(loadedMap)
    }
  }, [posts])

  const handleMainImageLoad = (e) => {
    e.target.classList.remove("blur")
    setMainImageLoaded(true)
  }

  const handlePostImageLoad = (idx, e) => {
    e.target.classList.remove("blur")
    setPostsImagesLoaded((prev) => ({
      ...prev,
      [idx]: true,
    }))
  }

  return (
    <ThemeProvider theme={themeViewnia}>
      <>
        <section className="first-post-grid-container">
          {loading || !lastPost ? (
            <>
              <section className="first-post-image">
                <Skeleton variant="rect" width="100%" height={500} animation="wave" />
              </section>
              <section className="first-post-body">
                <div className="first-post-info">
                  <Skeleton variant="text" width="40%" height={32} animation="wave" />
                  <Skeleton variant="text" width="80%" height={24} animation="wave" />
                  <Skeleton variant="text" width="60%" height={20} animation="wave" />
                  <Skeleton variant="text" width="30%" height={18} animation="wave" />
                </div>
              </section>
            </>
          ) : (
            <>
              <section className="first-post-image" style={{ position: "relative" }}>
                <figure>
                  <a href={lastPost.url} title="View Details">
                    <img
                      className="lazyload blur"
                      src={lastPost.cover_f || "/assets/phone.svg"}
                      data-original={lastPost.cover_f}
                      onLoad={handleMainImageLoad}
                      onError={(e) => {
                        e.target.onerror = null
                        e.target.src = "/assets/phone.svg"
                        e.target.classList.remove("blur")
                        setMainImageLoaded(true)
                      }}
                      style={{ width: "100%" }}
                      alt={lastPost.title}
                    />
                    {!mainImageLoaded && (
                      <Skeleton
                        variant="rect"
                        width="100%"
                        height={500}
                        animation="wave"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                        }}
                      />
                    )}
                  </a>
                </figure>
              </section>
              <section className="first-post-body">
                <div className="first-post-info">
                  <h4>{lastPost.label}</h4>
                  <h3>
                    <a href={lastPost.url} title="View Details">
                      {lastPost.title}
                    </a>
                  </h3>
                  <p className="subtile">{lastPost.subtitle}</p>
                  <p className="author">
                    <strong>{lastPost.author_name}</strong> | {lastPost.author_sm}
                  </p>
                </div>
              </section>
            </>
          )}
        </section>

        <section className="posts-grid-container">
          {loading || !posts || posts.length === 0
            ? Array.from(new Array(8)).map((_, idx) => (
                <article key={idx} className="post-grid">
                  <figure style={{ position: "relative" }}>
                    <Skeleton
                      variant="rect"
                      width="100%"
                      height={255}
                      animation="wave"
                      style={{ borderRadius: "15px" }}
                    />
                  </figure>
                  <div className="info-slide">
                    <Skeleton variant="text" width="60%" height={24} animation="wave" />
                    <Skeleton variant="text" width="80%" height={20} animation="wave" />
                    <Skeleton variant="text" width="40%" height={18} animation="wave" />
                    <Skeleton variant="text" width="50%" height={15} animation="wave" />
                  </div>
                </article>
              ))
            : posts.map((post, idx) => (
                <article key={idx} className="post-grid">
                  <a className="post-grid-link" href={post.url} title="View Details">
                    <figure style={{ position: "relative" }}>
                      <img
                        className="lazyload blur"
                        src={post.cover_f || "/assets/phone.svg"}
                        data-original={post.cover_f}
                        alt={post.title}
                        onLoad={(e) => handlePostImageLoad(idx, e)}
                        onError={(e) => {
                          e.target.onerror = null
                          e.target.src = "/assets/phone.svg"
                          e.target.classList.remove("blur")
                          handlePostImageLoad(idx, e)
                        }}
                        style={{ width: "100%" }}
                      />
                      {!postsImagesLoaded[idx] && (
                        <Skeleton
                          variant="rect"
                          width="100%"
                          height={255}
                          animation="wave"
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            borderRadius: "15px",
                          }}
                        />
                      )}
                    </figure>
                    <div className="info-slide">
                      <h4>{post.label}</h4>
                      <h3>
                        <a href={post.url} title="View Details">
                          {post.title}
                        </a>
                      </h3>
                      <p className="subtile">{post.subtitle}</p>
                      <p className="author">
                        <strong>{post.author_name}</strong> | {post.author_sm}
                      </p>
                    </div>
                  </a>
                </article>
              ))}
        </section>
      </>
    </ThemeProvider>
  )
}

export default PostBody
